import { useEffect, useState } from 'react'

import { getMs, isCordovaApp } from '@/helpers'
import { buildLegacyApiRequestUrl } from '@/helpers/legacy-api-request'

const BACKEND_CHECK_TIMEOUT_MS = getMs('5s')
const BACKEND_CHECK_INTERVAL_MS = getMs('30s')

const subscribers = new Set<() => void>()

let isMonitorStarted = false
let stopMonitor: (() => void) | null = null
let isNavigatorOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
let isBackendReachable = true
let backendCheckInFlight: Promise<void> | null = null

const emit = () => {
  subscribers.forEach((cb) => cb())
}

const getCombinedOnlineStatus = () => isNavigatorOnline && isBackendReachable

const setNavigatorOnline = (next: boolean) => {
  if (isNavigatorOnline === next) return
  isNavigatorOnline = next
  if (!next) {
    isBackendReachable = false
  }
  emit()
}

const setBackendReachable = (next: boolean) => {
  if (isBackendReachable === next) return
  isBackendReachable = next
  emit()
}

const checkBackendReachability = async () => {
  if (!isNavigatorOnline) {
    setBackendReachable(false)
    return
  }

  if (backendCheckInFlight) return backendCheckInFlight

  backendCheckInFlight = (async () => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), BACKEND_CHECK_TIMEOUT_MS)

    try {
      const response = await fetch(buildLegacyApiRequestUrl('client/visitor/info'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'data=' + encodeURIComponent(JSON.stringify({})),
        credentials: 'include',
        signal: controller.signal,
      })

      // Any non-5xx HTTP response means backend endpoint is reachable.
      setBackendReachable(response.status < 500)
    } catch {
      setBackendReachable(false)
    } finally {
      window.clearTimeout(timeoutId)
    }
  })().finally(() => {
    backendCheckInFlight = null
  })

  return backendCheckInFlight
}

const startOnlineMonitor = () => {
  if (isMonitorStarted || typeof window === 'undefined') return
  isMonitorStarted = true

  const runBackendCheck = () => {
    void checkBackendReachability()
  }

  const handleOnline = () => {
    setNavigatorOnline(true)
    runBackendCheck()
  }

  const handleOffline = () => {
    setNavigatorOnline(false)
  }

  const handleCordovaResume = () => {
    runBackendCheck()
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      runBackendCheck()
    }
  }

  const handleFocus = () => {
    runBackendCheck()
  }

  let intervalId: number | null = null

  if (isCordovaApp) {
    // В Cordova на некоторых устройствах/с фаерволом/плагинами события могут теряться
    // либо на `document`, либо на `window`.
    // Подписываемся на оба источника как на fallback для надежности.
    document.addEventListener('online', handleOnline)
    document.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('resume', handleCordovaResume, false)
  } else {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleFocus)

  intervalId = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      runBackendCheck()
    }
  }, BACKEND_CHECK_INTERVAL_MS)

  stopMonitor = () => {
    if (isCordovaApp) {
      document.removeEventListener('online', handleOnline)
      document.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('resume', handleCordovaResume, false)
    } else {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('focus', handleFocus)

    if (intervalId !== null) {
      window.clearInterval(intervalId)
      intervalId = null
    }

    stopMonitor = null
    isMonitorStarted = false
  }

  runBackendCheck()
}

export const useOnlineStatus = () => {
  const [ isOnline, setIsOnline ] = useState(getCombinedOnlineStatus())

  useEffect(() => {
    startOnlineMonitor()

    const syncState = () => {
      setIsOnline(getCombinedOnlineStatus())
    }

    subscribers.add(syncState)
    syncState()

    return () => {
      subscribers.delete(syncState)
      if (subscribers.size === 0 && stopMonitor) {
        stopMonitor()
      }
    }
  }, [])

  return isOnline
}
